import React, { Component } from 'react';
import Modal from '../../components/Modal';
import { urlMap, getAuthHeaders } from '../../../utils';

const MIN_QUERY_LEN = 2;
const MAX_RESULTS = 100;

const MODE_CONTAINS = 'contains';
const MODE_EXACT = 'exact';

const ESCAPE_RE = /[.*+?^${}()|[\]\\]/g;
const escapeRegExp = (value) => String(value || '').replace(ESCAPE_RE, '\\$&');

function getHighlightTerms(query, mode) {
  const normalized = String(query || '').trim();
  if (!normalized) return [];
  if (mode === MODE_EXACT) return [normalized];
  return normalized.split(/\s+/).filter(Boolean);
}

function highlightSnippet(snippet, query, mode) {
  const text = String(snippet || '');
  const terms = getHighlightTerms(query, mode).sort((a, b) => b.length - a.length);
  if (!text || terms.length === 0) return text;

  const pattern = terms.map(escapeRegExp).join('|');
  if (!pattern) return text;

  const regex = new RegExp(`(${pattern})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, idx) => {
    if (!part) return null;
    const isMatch = terms.some((term) => part.toLowerCase() === term.toLowerCase());
    return isMatch
      ? <mark key={`m-${idx}`}>{part}</mark>
      : <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>;
  });
}

export default class ProjectSceneSearchModal extends Component {
  state = {
    query: '',
    mode: MODE_CONTAINS,
    loading: false,
    hasSearched: false,
    results: [],
    error: null,
  };

  inputRef = React.createRef();
  debounceTimer = null;
  activeSearchSeq = 0;

  componentDidMount() {
    window.addEventListener('keydown', this.handleWindowKeyDown);
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.activeSearchSeq += 1;
      this.setState({
        query: '',
        mode: MODE_CONTAINS,
        loading: false,
        hasSearched: false,
        results: [],
        error: null,
      }, () => {
        if (this.inputRef.current) this.inputRef.current.focus();
      });
    }

    if (!this.props.isOpen && prevProps.isOpen) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.activeSearchSeq += 1;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleWindowKeyDown);
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.activeSearchSeq += 1;
  }

  handleWindowKeyDown = (event) => {
    if (!this.props.isOpen) return;
    if (event.key === 'Escape') {
      this.props.onClose?.();
    }
  };

  runSearch = async () => {
    const { projectId } = this.props;
    const { query, mode } = this.state;

    const needle = query.trim();
    if (needle.length < MIN_QUERY_LEN) {
      this.setState({ hasSearched: needle.length > 0, results: [], loading: false, error: null });
      return;
    }

    if (!projectId) {
      this.setState({ hasSearched: true, results: [], loading: false, error: 'Project is missing.' });
      return;
    }

    const seq = ++this.activeSearchSeq;
    this.setState({ loading: true, error: null });

    try {
      const response = await fetch(urlMap.searchProjectChapters(projectId, needle, mode, MAX_RESULTS), {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (seq !== this.activeSearchSeq) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const results = Array.isArray(payload?.results) ? payload.results : [];

      this.setState({
        loading: false,
        hasSearched: true,
        results,
        error: null,
      });
    } catch {
      if (seq !== this.activeSearchSeq) return;
      this.setState({
        loading: false,
        hasSearched: true,
        results: [],
        error: 'Search failed. Try again.',
      });
    }
  };

  scheduleSearch = () => {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(this.runSearch, 250);
  };

  handleQueryChange = (event) => {
    this.setState({ query: event.target.value }, this.scheduleSearch);
  };

  handleModeChange = (mode) => {
    this.setState({ mode }, this.scheduleSearch);
  };

  handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.runSearch();
    }
  };

  openChapter = (result) => {
    const { navigate, projectId, onClose } = this.props;
    const { query } = this.state;
    const pid = result.projectId || projectId;
    if (!navigate || !pid || !result.chapterId) return;
    const q = String(query || '').trim();
    const suffix = q ? `?query=${encodeURIComponent(q)}` : '';
    onClose?.();
    navigate(`/project/${pid}/chapter/${result.chapterId}${suffix}`);
  };

  renderResult = (result) => {
    const { query, mode } = this.state;
    const statusLabel = result.status === 'archived'
      ? 'Archived'
      : result.status === 'draft'
        ? 'Draft'
        : 'Active';

    const title = result.chapterTitle || result.title || 'Untitled Chapter';

    return (
      <li key={`${result.chapterId}-${result.matchedField}`} className="project-search-result-item">
        <button
          type="button"
          className="project-search-result-btn"
          onClick={() => this.openChapter(result)}
          title={`Open ${title}`}
        >
          <div className="project-search-result-top">
            <span className="project-search-result-title">
              {result.chapterNumber ? `Ch ${result.chapterNumber}. ` : ''}
              {title}
            </span>
            <span className={`project-search-status-badge status-${result.status}`}>
              {statusLabel}
            </span>
          </div>
          <div className="project-search-result-snippet">
            {result.snippet ? highlightSnippet(result.snippet, query, mode) : 'No preview available.'}
          </div>
        </button>
      </li>
    );
  };

  render() {
    const { isOpen, onClose } = this.props;
    const { query, mode, loading, hasSearched, results, error } = this.state;
    const trimmed = query.trim();

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="project-search-modal">
          <label className="project-search-label" htmlFor="project-scene-search-input">
            What are you looking for?
          </label>
          <input
            id="project-scene-search-input"
            ref={this.inputRef}
            type="text"
            className="project-search-input"
            value={query}
            onChange={this.handleQueryChange}
            onKeyDown={this.handleInputKeyDown}
            placeholder="Search across all chapters"
            aria-label="Search across all chapters"
          />

          <div className="project-search-mode-row" role="group" aria-label="Search mode">
            <button
              type="button"
              className={`project-search-mode-btn${mode === MODE_CONTAINS ? ' is-active' : ''}`}
              onClick={() => this.handleModeChange(MODE_CONTAINS)}
            >
              Contains
            </button>
            <button
              type="button"
              className={`project-search-mode-btn${mode === MODE_EXACT ? ' is-active' : ''}`}
              onClick={() => this.handleModeChange(MODE_EXACT)}
            >
              Exact phrase
            </button>
          </div>

          <div className="project-search-results" aria-live="polite">
            {!trimmed && !hasSearched && (
              <div className="project-search-empty-state">
                Type at least 2 characters to search chapter titles and content across Active, Draft, and Archived chapters.
              </div>
            )}

            {trimmed.length > 0 && trimmed.length < MIN_QUERY_LEN && (
              <div className="project-search-empty-state">Type at least {MIN_QUERY_LEN} characters.</div>
            )}

            {loading && <div className="project-search-empty-state">Searching...</div>}

            {!loading && error && <div className="project-search-empty-state">{error}</div>}

            {!loading && !error && hasSearched && trimmed.length >= MIN_QUERY_LEN && results.length === 0 && (
              <div className="project-search-empty-state">No matches found in this project.</div>
            )}

            {!loading && !error && results.length > 0 && (
              <>
                <div className="project-search-results-count">{results.length} result{results.length !== 1 ? 's' : ''}</div>
                <ul className="project-search-results-list">
                  {results.map(this.renderResult)}
                </ul>
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }
}
